'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Congregation, LocationGroup } from './types';
import { campusSlug } from './types';
import {
  fetchHomeCampus,
  fetchLiveStatus,
  fetchLocations,
  getMpWidgetToken,
  setHomeCampus,
  type CampusLive,
} from './api';
import {
  dispatchLiveStatus,
  getSelectedCampus,
  onCampusChange,
  setSelectedCampus,
} from './campus-storage';

export interface CampusSelection {
  /** The Church Hub API the selection reads from, for anything else the panel fetches */
  apiBaseUrl: string;
  locations: LocationGroup[];
  isLoading: boolean;
  error: boolean;
  selectedId: number | null;
  /** The selected congregation and its location's SVG, once locations have loaded */
  selectedCong: Congregation | null;
  selectedSvgUrl: string | null;
  select: (cong: Congregation, svgUrl: string | null) => void;
  /** Campuses streaming right now, by Congregation_ID; empty when nothing is live */
  liveByCampus: Map<number, CampusLive>;
}

/**
 * One selection model for both forms of the widget.
 *
 * Order of truth:
 *   1. The cookie paints first (instant, works signed out, shared across
 *      woodsidebible.org subdomains).
 *   2. If someone is signed in to MP, their campus in MinistryPlatform wins.
 *      The cookie is corrected to match and the page-level event fires so
 *      every other widget follows. If MP has nothing for them yet, the
 *      cookie's pick is pushed up so their other devices see it.
 *   3. Picking a campus writes the cookie immediately and MP in the background.
 *
 * Regaining focus re-reads the cookie, so a pick made on another subdomain or
 * tab shows up without a reload.
 */
export function useCampusSelection(apiBaseUrl: string): CampusSelection {
  const [locations, setLocations] = useState<LocationGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(
    () => getSelectedCampus()?.congregationId ?? null
  );
  // MP's answer, waiting for the locations list so the cookie can be filled in
  const [mpCampusId, setMpCampusId] = useState<number | null>(null);

  // Other widgets on the page
  useEffect(() => onCampusChange((campus) => setSelectedId(campus.congregationId)), []);

  // Other tabs and subdomains: re-read the cookie on focus
  useEffect(() => {
    function resync() {
      if (document.visibilityState === 'hidden') return;
      const cookie = getSelectedCampus();
      if (cookie && cookie.congregationId !== selectedId) {
        setSelectedId(cookie.congregationId);
        setSelectedCampus(cookie);
      }
    }
    window.addEventListener('focus', resync);
    document.addEventListener('visibilitychange', resync);
    return () => {
      window.removeEventListener('focus', resync);
      document.removeEventListener('visibilitychange', resync);
    };
  }, [selectedId]);

  // Campus list
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchLocations(apiBaseUrl)
      .then((data) => {
        if (cancelled) return;
        setLocations(data);
        setError(false);
      })
      .catch((err) => {
        console.error('CampusPicker: failed to load campuses', err);
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  // MinistryPlatform is the source of truth for a signed-in person
  useEffect(() => {
    if (!getMpWidgetToken()) return;
    let cancelled = false;
    fetchHomeCampus(apiBaseUrl).then((mp) => {
      if (cancelled || !mp) return;
      const cookie = getSelectedCampus();
      if (mp.congregationId) {
        if (cookie?.congregationId !== mp.congregationId) setMpCampusId(mp.congregationId);
      } else if (cookie) {
        // MP knows nothing yet: the browser's pick becomes the account's
        void setHomeCampus(apiBaseUrl, cookie.congregationId);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  // Apply MP's answer once we can name the campus
  useEffect(() => {
    if (!mpCampusId || locations.length === 0) return;
    for (const group of locations) {
      const cong = group.congregations.find((c) => c.id === mpCampusId);
      if (cong) {
        setSelectedId(cong.id);
        setSelectedCampus({
          congregationId: cong.id,
          name: cong.name,
          shortName: cong.short_name,
          slug: campusSlug(cong),
          svgUrl: group.svg_url,
        });
        break;
      }
    }
    setMpCampusId(null);
  }, [mpCampusId, locations]);

  const { selectedCong, selectedSvgUrl } = useMemo(() => {
    for (const group of locations) {
      const cong = group.congregations.find((c) => c.id === selectedId);
      if (cong) return { selectedCong: cong, selectedSvgUrl: group.svg_url };
    }
    return { selectedCong: null, selectedSvgUrl: null };
  }, [locations, selectedId]);

  // Poll live status the way the locations widget does: one batched call,
  // every 45s, only while the page is visible.
  const [liveByCampus, setLiveByCampus] = useState<Map<number, CampusLive>>(() => new Map());
  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      if (document.visibilityState === 'hidden') return;
      const map = await fetchLiveStatus(apiBaseUrl);
      if (cancelled) return;
      setLiveByCampus(map);
      // The header nav item is theme markup outside React; it listens for this
      dispatchLiveStatus(
        new Set(
          Array.from(map.entries())
            .filter(([, v]) => v.isLive)
            .map(([id]) => id)
        )
      );
    }
    refresh();
    const interval = setInterval(refresh, 45_000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [apiBaseUrl]);

  const select = useCallback(
    (cong: Congregation, svgUrl: string | null) => {
      setSelectedId(cong.id);
      setSelectedCampus({
        congregationId: cong.id,
        name: cong.name,
        shortName: cong.short_name,
        slug: campusSlug(cong),
        svgUrl,
      });
      void setHomeCampus(apiBaseUrl, cong.id);
    },
    [apiBaseUrl]
  );

  return {
    apiBaseUrl,
    locations,
    isLoading,
    error,
    selectedId,
    selectedCong,
    selectedSvgUrl,
    select,
    liveByCampus,
  };
}
